<script>
    import { preventDefault } from 'svelte/legacy';
    import { createEventDispatcher, onDestroy } from 'svelte';
    import * as userService from './service.svelte.js';
    import { notificate } from '../../services/notification.js';
    import { flatten422Errors } from '../../services/api.svelte.js';
    import { translateErrorMessages } from '../../services/api.svelte.js';
    import { form as svForm, field } from "svelte-forms";
    import { required } from 'svelte-forms/validators';
    import { user } from './user.state.svelte.js';
    import { format, locale } from 'svelte-i18n';

    const dispatch = createEventDispatcher();

    let { userToEdit = $bindable() } = $props();
    let formDataPromise = $state();
    let formData = $state({});
    let formIsSubmitted = false;
    let APIerror = $state(null);
    let formApiErrors = {};
    let inputElement;

    let languages = $derived(userService.getLangOptions($locale));

    if (!userToEdit) {
        formDataPromise = Promise.resolve({});
    } else {
        if(userToEdit.is_notification_enabled == "false"){
            userToEdit.is_notification_enabled = false;
        }
        formData = {
            id: userToEdit.id,
            role : userToEdit.role,
            is_notification_enabled: userToEdit.is_notification_enabled,
            language: userToEdit.language
        };
    }
    const emailField         = field('email', '', [required()]);
    const roleField          = field('role', userToEdit ? formData.role : "viewer", [required()]);
    const notificationsField = field('is_notification_enabled', userToEdit ? formData.is_notification_enabled : true, []);
    const languageField      = field('language', userToEdit ? formData.language : $locale, [required()]);
    const myForm = svForm(emailField, roleField, notificationsField, languageField);

    // Hard wipe svelte-forms cache when component is destroyed (closed via key refresh)
    onDestroy(() => {
        if (!userToEdit) {
            $emailField.value = '';
            if ($roleField) $roleField.value = 'viewer';
        }
        formApiErrors = {};
        APIerror = null;
        formIsSubmitted = false;
    });

    function shouldShowFieldError(field) {
        return (
            ((formIsSubmitted || field.dirty) && !field.valid)
            || (formApiErrors[field])
        );
    }

    function handleSubmit() {
        formIsSubmitted = true;
        formApiErrors = {};

        if (!$myForm.valid) {
            notificate('422');
            return false;
        }

        formData.email = $emailField.value;
        formData.role  = $roleField.value;
        formData.language = $languageField.value;

        if(userToEdit) {
            formData.is_notification_enabled = $notificationsField.value;
        }

        userService.save(formData)
        .then(user => {
                dispatch("save", {user});
                
                // Force clear immediately upon success
                if (!userToEdit) {
                    $emailField.value = '';
                }
                
                formData = {};
                formIsSubmitted = false;
                formApiErrors = {};
                notificate('save_ok', "toast", {data: user.email});
                return true;
            })
            .catch(errors => {
                if(typeof(errors.detail) == 'string'){
                   APIerror = translateErrorMessages(errors.detail);
                } else {
                    if(errors.detail && errors.detail[0].msg == 'value is not a valid email address') {
                        APIerror = translateErrorMessages(errors.detail[0].msg);
                    }
                    else if(!errors.detail && (errors.includes("The workspace has reached the allowed limit") && errors.includes("target user"))){
                        APIerror = translateErrorMessages(errors);
                    }
                    else {
                        formApiErrors = flatten422Errors(errors);
                    }
                }
                return false;
            })
        ;
        formIsSubmitted = false;
        formApiErrors = {}
        APIerror = null;
        return true;
    }
    
    function handleCloseModal(){
        formIsSubmitted = false;
        // Force clear on cancel just to be completely sure
        if (!userToEdit) $emailField.value = '';
        dispatch('cancel'); 
    }

    const handleKeydown = event => {
        if(event.key == "Escape"){
            handleCloseModal();
        }
        else {
            return;
        }
    }
</script>

<svelte:window onkeydown={handleKeydown}/>

<form role="form" novalidate onsubmit={preventDefault(handleSubmit)}>
    <div class="card">
        <div class="card-body">
            {#if !userToEdit || user.data.id != userToEdit.id}
                {#await formDataPromise then formDataUnused}
                <div class="mb-3">
                    <label for="email">Email<span title="Requerido" class="text-danger">*</span></label>
                    {#if !userToEdit}
                        <input
                            type="email"
                            class="form-control t-input-user-email"
                            id="email"
                            name="email"
                            placeholder="Email"
                            bind:value={$emailField.value}
                            disabled={formData.id}
                            class:is-invalid={shouldShowFieldError($emailField)}
                        />
                        {#if shouldShowFieldError($emailField)}
                            {#if $myForm.hasError('email.required')}
                            <div class="invalid-feedback">
                                {$format("required")}
                            </div>
                            {/if}
                        {/if}
                    {:else}
                        <span>{userToEdit.email}</span>
                    {/if}
                </div>

                <div class="mb-3">
                    <label for="role">{$format("page.me.role")}</label>
                    <select
                        id="role"
                        name="role"
                        class="t-input-user-role form-control"
                        bind:value={$roleField.value}
                    >
                    {#each Object.entries(userService.roles) as [value, label]}
                        {#if value != "uploader"}
                            <option value={value}>
                                {$format(`page.user.roles.${value}`)}
                            </option>
                        {/if}
                    {/each}
                    </select>
                </div>

                <div class="mb-3">
                    <label for="language">{$format("form.fields.language")}</label>
                    <select id="language" class="form-control" bind:value={$languageField.value}>
                        {#each languages as { code, name }}
                            <option value="{code}">{name}</option>
                        {/each}
                    </select>
                </div>

                {#if userToEdit}
                    <div class="mb-3">
                        <div class="form-check form-switch">
                            <input type="checkbox" bind:checked="{$notificationsField.value}" onclick={()=>$notificationsField.value = !$notificationsField.value} class="form-check-input" id="notifications-checkbox">
                            <label class="custom-control-label" for="notifications-checkbox"></label>{$format("page.me.enable_notifications")}
                        </div>
                    </div>
                {/if}
                {/await}
            {:else}
                <div>{$format("errors.own_user")}</div>
            {/if}
            {#if APIerror}
                <div class="mt-3 text-danger" style="font-size: 80%;">{APIerror}</div>
            {/if}
        </div>
        <div class="card-footer">
            {#if !userToEdit || user.data.id != userToEdit.id}<button type="submit" class="btn btn-primary t-submit-user-btn">{$format("buttons.save")}</button>{/if}
            <button type="button" class="btn btn-default" onclick={handleCloseModal}>{$format("buttons.cancel")}</button>
        </div>
        {#await formDataPromise}
        <div class="overlay">
            <i class="fas fa-2x fa-sync-alt fa-spin"></i>
        </div>
        {/await}
    </div>
</form>